import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Button, Alert } from 'react-bootstrap';
import { LoginManager } from '../../../core/login-manager';
import { LanguageProvider } from '../../../core/language-provider';

const CurrentSession = () => {
    const [sessionInfo, setSessionInfo] = useState(null);
    const [refreshTime, setRefreshTime] = useState(new Date());

    useEffect(() => {
        loadSessionInfo();
    }, []);

    const loadSessionInfo = async () => {
        try {
            const userInfo = LoginManager.getUserInfo();
            const sid = await LoginManager.getSid();
            const ltsid = await LoginManager.getLtsid();

            setSessionInfo({
                userInfo: userInfo,
                sid: sid,
                ltsid: ltsid,
                sidMasked: maskString(sid),
                ltsidMasked: maskString(ltsid)
            });
            setRefreshTime(new Date());
        } catch (error) {
            console.error('Error loading session info:', error);
        }
    };

    const maskString = (str) => {
        if (!str) return '-';
        if (str.length <= 8) return '****';
        return str.substring(0, 4) + '****' + str.substring(str.length - 4);
    };

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleString();
    };

    const getUserLevelLabel = (level) => {
        switch (level) {
            case 0: return { label: 'Admin', variant: 'danger' };
            case 1: return { label: 'Standard', variant: 'primary' };
            case 2: return { label: 'Restricted', variant: 'warning' };
            default: return { label: `Level ${level}`, variant: 'secondary' };
        }
    };

    const copyToClipboard = async (text, fieldName) => {
        try {
            await navigator.clipboard.writeText(text);
            alert(`${fieldName} copied to clipboard`);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    if (!sessionInfo) {
        return (
            <div className="mt-4 text-center">
                <p className="text-muted">Loading session info...</p>
            </div>
        );
    }

    const { userInfo, sid, ltsid, sidMasked, ltsidMasked } = sessionInfo;
    const levelInfo = getUserLevelLabel(userInfo?.level);

    return (
        <div className="mt-4">
            {/* Session Status */}
            <Alert variant="info" className="mb-3">
                <div className="d-flex align-items-center justify-content-between">
                    <div>
                        <strong>Session Status</strong>
                        <Badge bg="success" className="ms-2">Active</Badge>
                    </div>
                    <small className="text-muted">
                        Last refresh: {formatDate(refreshTime)}
                    </small>
                </div>
            </Alert>

            {/* User Info Card */}
            <Card className="mb-4">
                <Card.Header>
                    <h5>User Information</h5>
                </Card.Header>
                <Card.Body>
                    <Table bordered size="sm">
                        <tbody>
                            <tr>
                                <td style={{width: '30%'}}><strong>User ID</strong></td>
                                <td><code>{userInfo?.id || '-'}</code></td>
                            </tr>
                            <tr>
                                <td><strong>Username</strong></td>
                                <td>{userInfo?.username || '-'}</td>
                            </tr>
                            <tr>
                                <td><strong>Name</strong></td>
                                <td>{userInfo?.name || '-'} {userInfo?.surname || ''}</td>
                            </tr>
                            <tr>
                                <td><strong>Email</strong></td>
                                <td>{userInfo?.email || '-'}</td>
                            </tr>
                            <tr>
                                <td><strong>Level</strong></td>
                                <td>
                                    <Badge bg={levelInfo.variant}>{levelInfo.label}</Badge>
                                    <small className="text-muted ms-2">(level {userInfo?.level})</small>
                                </td>
                            </tr>
                            {userInfo?.idUserGroup && (
                                <tr>
                                    <td><strong>User Group ID</strong></td>
                                    <td><code>{userInfo.idUserGroup}</code></td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            {/* Session Tokens Card */}
            <Card className="mb-4">
                <Card.Header>
                    <h5>Session Tokens</h5>
                </Card.Header>
                <Card.Body>
                    <Table bordered size="sm">
                        <tbody>
                            <tr>
                                <td style={{width: '30%'}}><strong>Session ID (SID)</strong></td>
                                <td>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <code>{sidMasked}</code>
                                        <Button
                                            size="sm"
                                            variant="outline-secondary"
                                            onClick={() => copyToClipboard(sid, 'SID')}
                                        >
                                            Copy
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td><strong>Long-Term SID (LTSID)</strong></td>
                                <td>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <code>{ltsidMasked}</code>
                                        <Button
                                            size="sm"
                                            variant="outline-secondary"
                                            onClick={() => copyToClipboard(ltsid, 'LTSID')}
                                        >
                                            Copy
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </Table>
                    <small className="text-muted">
                        These tokens are used to authenticate your session. Do not share them.
                    </small>
                </Card.Body>
            </Card>

            {/* Local Storage Info */}
            <Card className="mb-4">
                <Card.Header>
                    <h5>Storage Info</h5>
                </Card.Header>
                <Card.Body>
                    <Table bordered size="sm">
                        <tbody>
                            <tr>
                                <td style={{width: '30%'}}><strong>SID in localStorage</strong></td>
                                <td>
                                    <Badge bg={localStorage.getItem('sid') ? 'success' : 'warning'}>
                                        {localStorage.getItem('sid') ? 'Present' : 'Missing'}
                                    </Badge>
                                </td>
                            </tr>
                            <tr>
                                <td><strong>LTSID in localStorage</strong></td>
                                <td>
                                    <Badge bg={localStorage.getItem('ltsid') ? 'success' : 'warning'}>
                                        {localStorage.getItem('ltsid') ? 'Present' : 'Missing'}
                                    </Badge>
                                </td>
                            </tr>
                            <tr>
                                <td><strong>UserInfo in localStorage</strong></td>
                                <td>
                                    <Badge bg={localStorage.getItem('userInfo') ? 'success' : 'warning'}>
                                        {localStorage.getItem('userInfo') ? 'Present' : 'Missing'}
                                    </Badge>
                                </td>
                            </tr>
                            <tr>
                                <td><strong>Deeplink saved</strong></td>
                                <td>
                                    {localStorage.getItem('deeplink') ? (
                                        <code style={{fontSize: '0.8rem'}}>{localStorage.getItem('deeplink')}</code>
                                    ) : (
                                        <span className="text-muted">None</span>
                                    )}
                                </td>
                            </tr>
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            {/* All UserInfo Fields */}
            {userInfo && Object.keys(userInfo).length > 0 && (
                <Card className="mb-4">
                    <Card.Header>
                        <h5>All User Info Fields</h5>
                    </Card.Header>
                    <Card.Body>
                        <div style={{maxHeight: '300px', overflow: 'auto'}}>
                            <pre style={{
                                backgroundColor: 'rgba(100,100,100,0.1)',
                                padding: '1rem',
                                borderRadius: '0.375rem',
                                fontSize: '0.8rem',
                                lineHeight: '1.4',
                                margin: 0
                            }}>
                                {JSON.stringify(userInfo, null, 2)}
                            </pre>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* Actions */}
            <div className="text-center mb-4">
                <Button
                    variant="info"
                    onClick={loadSessionInfo}
                    className="me-2"
                >
                    Refresh Session Info
                </Button>
                <Button
                    variant="warning"
                    onClick={() => {
                        LoginManager.refreshSid();
                        setTimeout(loadSessionInfo, 1000);
                    }}
                >
                    Force Refresh SID
                </Button>
            </div>
        </div>
    );
};

export default CurrentSession;
